<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.activities_index,
                    },
                    {
                        name: $tc('messages.weight'),
                        href: null,
                    },
                ]"
            />
        </template>
        <form :action="meta.links.index" method="GET" @submit.prevent="filter" class="p-4">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <SelectWithPreferences
                    required
                    class="sm:col-span-1"
                    :options="options.groups"
                    name="group_id"
                    :label="$tc('messages.groups', 1)"
                    property-name="text"
                    :preferences="defaults.preferences.group_ids"
                    :default-option="form.group_id"
                    :autoselect="false"
                    property-value="value"
                    v-model="form.group_id"
                />

                <SelectWithPreferences
                    required
                    class="sm:col-span-2"
                    :options="options.categories"
                    name="category_id"
                    :label="$tc('messages.domains', 1)"
                    property-name="text"
                    :preferences="[]"
                    :default-option="form.category_id"
                    :autoselect="false"
                    property-value="value"
                    v-model="form.category_id"
                />

                <div class="sm:col-span-1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.trimester')" for="trimester" />
                        <FormSelect
                            required
                            id="trimester"
                            v-model="form.trimester"
                            :options="options.trimesters"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.trimester" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2" v-if="false">
                    <div class="mt-1">
                        <FormLabel
                            :value="$t('messages.include_without_programming')"
                            for="include_without_programming"
                        />
                        <input
                            id="include_without_programming"
                            type="checkbox"
                            v-model="form.include_without_programming"
                            name="include_without_programming"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <FormError :value="form.errors.include_without_programming" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.where_has_grades_of_group')" for="where_has_grades_of_group" />
                        <input
                            id="where_has_grades_of_group"
                            type="checkbox"
                            v-model="form.where_has_grades_of_group"
                            name="where_has_grades_of_group"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <FormError :value="form.errors.where_has_grades_of_group" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="activities.length > 0">
            <div>
                <apexchart
                    :type="comparisonChart.chart.type"
                    :height="comparisonChart.chart.height"
                    :series="comparisonChart.series"
                    :options="comparisonChart"
                />
            </div>
        </div>

        <div v-if="activities.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div v-else class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    #
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.title') }}
                                </th>
                                <th
                                    v-for="dimension in dimensions"
                                    :key="dimension.id"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span :title="dimension.title">{{ dimension.abbreviation }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(activity, index) in activities"
                                :key="activity.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ activity.number }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5">
                                    <div class="flex items-center space-x-1">
                                        <ActivityIcon :type="activity.type" :title="activity.type_display" />
                                        <inertia-link :href="activity.url" class="text-indigo-500">
                                            {{ activity.title }}
                                        </inertia-link>
                                    </div>
                                </td>

                                <td
                                    v-for="dimension in activity.dimensions"
                                    :key="`${activity.id}_${dimension.id}`"
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                >
                                    <span v-if="dimension.percent">{{ dimension.percent }}%</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import SelectWithPreferences from '@/components/SelectWithPreferences.vue';
import EmptyState from '@/components/EmptyState.vue';
import ActivityIcon from '@/components/ActivityIcon.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        EmptyState,
        FormLabel,
        FormSelect,
        FormError,
        SelectWithPreferences,
        ActivityIcon,
        PanelPage,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'activities', 'comparisonChart', 'options', 'filters', 'dimensions', 'defaults'],
    data() {
        return {
            form: this.$inertia.form({
                trimester: this.filters.trimester,
                group_id: this.filters.group_id,
                category_id: this.filters.category_id,
                where_has_grades_of_group: this.filters.where_has_grades_of_group,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
